import {
  Edit,
  SimpleForm,
  useDataProvider,
  useEditContext,
  useNotify,
  useSaveContext,
} from "react-admin";
import { Alert } from "@mui/material";
import SaveForm from "./SaveForm";
import BackButtonToolbar from "features/BackButtonToolbar";
import InactiveDeleteOptionEditToolbar from "features/EditToolbar";
import { useNotifyError } from "utils/useNotifyError";
import { DormitoryCampaignStatuses } from "utils/constants";

const STRUCTURAL_FIELDS = ["applicantType", "accommodationFrom", "accommodationTo"];

const STALE_LOCK_MESSAGE =
  "По кампанията вече има подадени молби. Аудиторията и датите за настаняване не могат да се променят.";

const isTerminal = status =>
  status === DormitoryCampaignStatuses.Finished || status === DormitoryCampaignStatuses.Cancelled;

const terminalMessage = status =>
  status === DormitoryCampaignStatuses.Finished
    ? "Кампанията е приключена и не може да се редактира."
    : "Кампанията е отказана и не може да се редактира.";

// Only an explicit `false` from the backend unlocks structural editing. While loading, after a
// failed refresh, or when the flag is absent, the structural fields stay locked (defensive).
const isStructuralLocked = campaign => campaign?.structuralFieldsLocked !== false;

const CampaignEditForm = () => {
  const { record, resource } = useEditContext();
  const { save } = useSaveContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useNotifyError();

  if (!record) {
    return null;
  }

  if (isTerminal(record.status)) {
    return (
      <SimpleForm toolbar={false}>
        <Alert
          severity="info"
          sx={{ mb: 2, width: "100%" }}
        >
          {terminalMessage(record.status)}
        </Alert>
        <SaveForm
          mode="edit"
          readOnly
        />
      </SimpleForm>
    );
  }

  // Refresh the campaign right before saving: if applications arrived while the editor was open,
  // stop the submission of changed structural values instead of silently discarding the edits.
  const handleSubmit = async values => {
    const structuralChanged = STRUCTURAL_FIELDS.some(field => values[field] !== record[field]);

    if (structuralChanged) {
      try {
        const { data: fresh } = await dataProvider.getOne(resource, { id: record.id });
        if (isStructuralLocked(fresh)) {
          notify(STALE_LOCK_MESSAGE, { type: "warning" });
          return;
        }
      } catch (error) {
        notifyError(error);
        return;
      }
    }

    try {
      await save(values);
    } catch {
      // The data provider already surfaced the error; pessimistic mode keeps the user on the form.
    }
  };

  return (
    <SimpleForm
      toolbar={<InactiveDeleteOptionEditToolbar />}
      onSubmit={handleSubmit}
    >
      <SaveForm
        mode="edit"
        structuralLocked={isStructuralLocked(record)}
      />
    </SimpleForm>
  );
};

const EditCampaign = () => {
  return (
    <Edit
      title="Редактиране на кампания"
      mutationMode="pessimistic"
      transform={({
        nameBg,
        nameEn,
        applicantType,
        documentsFrom,
        documentsTo,
        commissionChairEmployeeId,
        sessionDate,
        accommodationFrom,
        accommodationTo,
      }) => ({
        nameBg,
        nameEn,
        applicantType,
        documentsFrom,
        documentsTo,
        commissionChairEmployeeId,
        sessionDate,
        accommodationFrom,
        accommodationTo,
      })}
      actions={<BackButtonToolbar />}
    >
      <CampaignEditForm />
    </Edit>
  );
};

export default EditCampaign;
